import type { CancelablePromise } from "../client"
import { OpenAPI } from "../client"
import { request as __request } from "../client/core/request"

export type SalesOfferItemCreateDTO = {
  product_id: string
  warehouse_lot_id?: string
  location_code?: string
  lot_no?: string
  qty: number
  price: number
  vat_rate: number
  discount_type?: string
  discount_value?: number
  margin_percent?: number
  cost_price_netto?: number
}

export type SalesOfferItemUpsertDTO = SalesOfferItemCreateDTO & {
  id?: string
}

export type SalesOfferCreateDTO = {
  warehouse_id: string
  contragent_id?: string
  contractor: string
  description?: string
  items: SalesOfferItemCreateDTO[]
}

export type InitSalesOfferDraftDTO = {
  warehouse_id: string
}

export type SalesOfferUpdateDTO = {
  warehouse_id?: string | null
  contragent_id?: string | null
  contractor?: string | null
  description?: string | null
  items: SalesOfferItemUpsertDTO[]
}

export type SalesOfferListItemDTO = {
  id: string
  number: string
  status: string
  warehouse_code: string
  warehouse_id: string
  contragent_id?: string | null
  owner_id: string
  owner_acronym: string
  contractor: string
  related_zs_id?: string | null
  related_zs_no?: string
  related_wz_id?: string | null
  related_wz_no?: string
  created_at: string
}

export type SalesOfferDetailDTO = {
  id: string
  number: string
  status: string
  contragent_id?: string | null
  contractor: string
  description?: string
  created_at: string
  confirmed_at?: string | null
  cancelled_at?: string | null
  warehouse: { id: string; code: string; name: string }
  owner: { id: string; name: string; acronym: string }
  confirmed_by?: { id: string; name: string; acronym: string } | null
  cancelled_by?: { id: string; name: string; acronym: string } | null
  related_zs_id?: string | null
  related_zs_no?: string
  related_wz_id?: string | null
  related_wz_no?: string
  items: Array<{
    id: string
    warehouse_lot_id?: string
    location_code?: string
    lot_no?: string
    production_date?: string
    expiration_date?: string
    product: {
      id: string
      sku: string
      name: string
      unit: string
      EAN: string
    }
    qty: number
    vat_rate: number
    price: number
    price_netto: number
    price_brutto: number
    cost_price_netto: number
    discount_type: string
    discount_value: number
    margin_percent: number
    value_netto: number
    value_brutto: number
  }>
}

export type SalesOfferResponseDTO = {
  id: string
  number: string
  status: string
  created_at: string
}

export type SalesOfferConversionResponseDTO = {
  id: string
  number: string
  status: string
  created_at: string
  notices?: string[]
}

export const SalesOffersService = {
  getAllSalesOffers: (
    year?: number,
    month?: number,
  ): CancelablePromise<SalesOfferListItemDTO[]> =>
    __request(OpenAPI, {
      method: "GET",
      url: "/v1/sales-offers/get-all",
      query: { year, month },
    }),

  getSalesOfferByID: (id: string): CancelablePromise<SalesOfferDetailDTO> =>
    __request(OpenAPI, {
      method: "GET",
      url: "/v1/sales-offers/{id}",
      path: { id },
    }),

  createSalesOffer: (
    requestBody: SalesOfferCreateDTO,
  ): CancelablePromise<SalesOfferResponseDTO> =>
    __request(OpenAPI, {
      method: "POST",
      url: "/v1/sales-offers/create",
      body: requestBody,
      mediaType: "application/json",
    }),

  initSalesOfferDraft: (
    requestBody: InitSalesOfferDraftDTO,
  ): CancelablePromise<SalesOfferResponseDTO> =>
    __request(OpenAPI, {
      method: "POST",
      url: "/v1/sales-offers/drafts",
      body: requestBody,
      mediaType: "application/json",
    }),

  updateSalesOfferByID: (
    id: string,
    requestBody: SalesOfferUpdateDTO,
  ): CancelablePromise<SalesOfferResponseDTO> =>
    __request(OpenAPI, {
      method: "PATCH",
      url: "/v1/sales-offers/{id}",
      path: { id },
      body: requestBody,
      mediaType: "application/json",
    }),

  confirmSalesOfferByID: (
    id: string,
  ): CancelablePromise<SalesOfferResponseDTO> =>
    __request(OpenAPI, {
      method: "POST",
      url: "/v1/sales-offers/{id}/confirm",
      path: { id },
    }),

  cancelSalesOfferByID: (
    id: string,
  ): CancelablePromise<SalesOfferResponseDTO> =>
    __request(OpenAPI, {
      method: "POST",
      url: "/v1/sales-offers/{id}/cancel",
      path: { id },
    }),

  reopenSalesOfferById: ({
    id,
  }: {
    id: string
  }): CancelablePromise<SalesOfferResponseDTO> =>
    __request(OpenAPI, {
      method: "POST",
      url: "/v1/sales-offers/{id}/reopen",
      path: { id },
    }),

  deleteSalesOfferById: ({ id }: { id: string }): CancelablePromise<void> =>
    __request(OpenAPI, {
      method: "DELETE",
      url: "/v1/sales-offers/{id}",
      path: { id },
    }),

  createSalesOrderFromOffer: (
    id: string,
  ): CancelablePromise<SalesOfferConversionResponseDTO> =>
    __request(OpenAPI, {
      method: "POST",
      url: "/v1/sales-offers/{id}/create-zs",
      path: { id },
    }),

  createReleaseFromOffer: (
    id: string,
  ): CancelablePromise<SalesOfferConversionResponseDTO> =>
    __request(OpenAPI, {
      method: "POST",
      url: "/v1/sales-offers/{id}/create-wz",
      path: { id },
    }),
}
